import { useId, CSSProperties, useState } from 'react'

interface AnalyticalBalanceProps {
  mass?: number // in grams
  reading?: number // alias for mass (for compatibility)
  tared?: boolean
  onTare?: () => void
  onPlace?: () => void
  onClick?: () => void // General click handler
  hasSample?: boolean
  stable?: boolean // For display purposes
  unit?: string // Display unit
  disabled?: boolean
  className?: string
}

const AnalyticalBalance = ({
  mass = 0.0000,
  reading,
  tared = false,
  onTare,
  onPlace,
  onClick,
  hasSample = false,
  stable = true,
  unit = 'g',
  disabled = false,
  className = ''
}: AnalyticalBalanceProps) => {
  const id = useId()
  const [isHovered, setIsHovered] = useState(false)

  // Use reading if provided (for compatibility), otherwise use mass
  const displayMassValue = reading !== undefined ? reading : mass

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : (onClick || onPlace) ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  const panStyle: CSSProperties = {
    cursor: disabled ? 'default' : onPlace ? 'pointer' : 'default'
  }

  const buttonStyle: CSSProperties = {
    cursor: disabled ? 'default' : 'pointer'
  }

  // Format mass to 4 decimal places
  const displayMass = displayMassValue.toFixed(4)

  return (
    <svg
      viewBox="0 0 200 150"
      width={260}
      height={200}
      style={containerStyle}
      className={className}
      onClick={!disabled && onClick ? onClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <defs>
        {/* Main body gradient */}
        <linearGradient id={`body-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#E8E8E8" />
          <stop offset="50%" stopColor="#D0D0D0" />
          <stop offset="100%" stopColor="#B8B8B8" />
        </linearGradient>

        {/* Dark body accent */}
        <linearGradient id={`body-dark-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#2C2C2C" />
        </linearGradient>

        {/* Glass chamber gradient */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.4)" />
          <stop offset="10%" stopColor="rgba(255,255,255,0.2)" />
          <stop offset="90%" stopColor="rgba(255,255,255,0.2)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.4)" />
        </linearGradient>

        {/* Pan gradient */}
        <linearGradient id={`pan-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#C0C0C0" />
          <stop offset="50%" stopColor="#A8A8A8" />
          <stop offset="100%" stopColor="#909090" />
        </linearGradient>

        {/* Display glow */}
        <filter id={`glow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-5%" y="-5%" width="115%" height="120%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.3" />
        </filter>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Base platform */}
        <rect
          x="15"
          y="125"
          width="170"
          height="12"
          fill={`url(#body-dark-${id})`}
          rx="3"
        />

        {/* Main body */}
        <rect
          x="20"
          y="85"
          width="160"
          height="42"
          fill={`url(#body-${id})`}
          rx="5"
        />

        {/* Control panel area */}
        <rect
          x="25"
          y="88"
          width="70"
          height="36"
          fill="#2A2A2A"
          rx="3"
        />

        {/* Display */}
        <rect
          x="30"
          y="92"
          width="60"
          height="18"
          fill="#001a00"
          rx="2"
        />

        {/* Mass reading */}
        <g filter={`url(#glow-${id})`}>
          <text
            x="60"
            y="106"
            fontSize="12"
            fill="#00A86B"
            textAnchor="middle"
            fontFamily="'Courier New', monospace"
            fontWeight="bold"
          >
            {displayMass}
          </text>
        </g>

        {/* Unit label */}
        <text
          x="82"
          y="106"
          fontSize="7"
          fill="#00A86B"
          fontFamily="'Courier New', monospace"
        >
          g
        </text>

        {/* Control buttons */}
        <g style={buttonStyle}>
          {/* Tare button */}
          <rect
            x="32"
            y="112"
            width="24"
            height="9"
            fill={tared ? '#00A86B' : '#4A4A4A'}
            rx="2"
            onClick={!disabled && onTare ? onTare : undefined}
          />
          <text
            x="44"
            y="119"
            fontSize="5"
            fill="white"
            textAnchor="middle"
            fontWeight="bold"
          >
            TARE
          </text>

          {/* Zero button */}
          <rect
            x="60"
            y="112"
            width="24"
            height="9"
            fill="#4A4A4A"
            rx="2"
          />
          <text
            x="72"
            y="119"
            fontSize="5"
            fill="white"
            textAnchor="middle"
            fontWeight="bold"
          >
            ZERO
          </text>
        </g>

        {/* Status indicators */}
        <g>
          {/* Stability indicator */}
          <circle
            cx="100"
            cy="100"
            r="4"
            fill={hasSample ? '#00A86B' : '#666'}
          />
          <text x="100" y="118" fontSize="5" fill="#888" textAnchor="middle">STABLE</text>

          {/* Tare indicator */}
          <circle
            cx="115"
            cy="100"
            r="4"
            fill={tared ? '#00A86B' : '#666'}
          />
          <text x="115" y="118" fontSize="5" fill="#888" textAnchor="middle">TARE</text>
        </g>

        {/* Brand area */}
        <rect
          x="130"
          y="95"
          width="45"
          height="25"
          fill="#FF5A36"
          rx="3"
        />
        <text
          x="152"
          y="108"
          fontSize="7"
          fill="white"
          textAnchor="middle"
          fontWeight="bold"
          fontFamily="Arial, sans-serif"
        >
          PRECISION
        </text>
        <text
          x="152"
          y="116"
          fontSize="5"
          fill="white"
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
        >
          0.0001g
        </text>

        {/* Glass chamber - weighing area */}
        <rect
          x="55"
          y="30"
          width="90"
          height="55"
          fill="rgba(240,245,250,0.1)"
          stroke="rgba(200,220,240,0.6)"
          strokeWidth="2"
          rx="3"
        />

        {/* Glass side panels */}
        <rect
          x="55"
          y="30"
          width="8"
          height="55"
          fill={`url(#glass-${id})`}
        />
        <rect
          x="137"
          y="30"
          width="8"
          height="55"
          fill={`url(#glass-${id})`}
        />

        {/* Glass top */}
        <rect
          x="55"
          y="25"
          width="90"
          height="8"
          fill="rgba(200,220,240,0.3)"
          rx="2"
        />

        {/* Glass back panel */}
        <rect
          x="57"
          y="32"
          width="86"
          height="51"
          fill="rgba(220,230,240,0.15)"
        />

        {/* Weighing pan */}
        <g
          style={panStyle}
          onClick={!disabled && onPlace ? onPlace : undefined}
        >
          <ellipse
            cx="100"
            cy="70"
            rx="30"
            ry="5"
            fill={`url(#pan-${id})`}
            stroke="#888"
            strokeWidth="1"
          />
          {/* Pan edge highlight */}
          <ellipse
            cx="100"
            cy="70"
            rx="28"
            ry="4"
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="1"
          />

          {/* Pan support */}
          <rect
            x="96"
            y="74"
            width="8"
            height="8"
            fill="#808080"
          />

          {/* Hover effect on pan */}
          {isHovered && onPlace && (
            <ellipse
              cx="100"
              cy="70"
              rx="32"
              ry="6"
              fill="none"
              stroke="#FF5A36"
              strokeWidth="2"
              opacity="0.5"
            />
          )}

          {/* Sample indicator */}
          {hasSample && (
            <g>
              <ellipse
                cx="100"
                cy="65"
                rx="15"
                ry="5"
                fill="#8B7355"
              />
              <ellipse
                cx="100"
                cy="62"
                rx="12"
                ry="4"
                fill="#A08060"
              />
            </g>
          )}
        </g>

        {/* Draft shield doors (represented as lines on sides) */}
        <line x1="55" y1="40" x2="55" y2="75" stroke="rgba(150,180,200,0.5)" strokeWidth="1" />
        <line x1="145" y1="40" x2="145" y2="75" stroke="rgba(150,180,200,0.5)" strokeWidth="1" />

        {/* Level bubble */}
        <circle
          cx="165"
          cy="130"
          r="6"
          fill="white"
          stroke="#888"
          strokeWidth="1"
        />
        <circle
          cx="165"
          cy="130"
          r="3"
          fill="#00A86B"
        />
        <circle
          cx="165"
          cy="130"
          r="1.5"
          fill="white"
          opacity="0.6"
        />

        {/* Feet */}
        <ellipse cx="35" cy="137" rx="8" ry="3" fill="#3A3A3A" />
        <ellipse cx="165" cy="137" rx="8" ry="3" fill="#3A3A3A" />
      </g>

      {/* Label */}
      <text
        x="100"
        y="148"
        fontSize="9"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Analytical Balance
      </text>
    </svg>
  )
}

export default AnalyticalBalance
